/* eslint no-underscore-dangle: ["error", { "allow": ["_created_at", "_updated_at"] }] */
import axios, {
  AxiosRequestConfig,
  CancelTokenSource,
  InternalAxiosRequestConfig,
} from 'axios';
import { BACKOFFICE_API } from './routes';

function transformBackofficeUrl(url: string): string {
  const match = url.match(/\/backoffice\/(authors|literature)\/search/);

  if (!match) {
    return url;
  }

  const type = match[1];

  const newUrl = url
    .replace('/facets', '')
    .replace('q=', 'search=')
    .replace(`/backoffice/${type}/search`, `/workflows/${type}/search`);

  return newUrl;
}

// `Proxy` could be used instead of wrapper class, depending on the browser support
export class HttpClientWrapper {
  private httpClient;

  private activeCancelManagersById = new Map<string, CancelTokenSource>();

  constructor(config: AxiosRequestConfig) {
    this.httpClient = axios.create({
      ...config,
    });
    this.setupInterceptors();
  }

  private static handleRequestUrlInterceptor(
    config: InternalAxiosRequestConfig
  ) {
    const url = config.url || '';
    if (url.startsWith('/backoffice')) {
      config.baseURL = BACKOFFICE_API;
      config.withCredentials = true;
      config.url = transformBackofficeUrl(url);
      config.headers.Accept = 'application/vnd.inspirehep.backoffice-ui+json';
    } else if (url.startsWith('/ai')) {
      config.baseURL = '/';
    } else {
      config.baseURL = '/api';
    }
    return config;
  }

  private setupInterceptors() {
    this.httpClient.interceptors.request.use(
      HttpClientWrapper.handleRequestUrlInterceptor
    );
  }

  /**
   * Allows requests to be tracked by id
   * and there can't be multiple active requests with the same id
   * current one is cancelled when a newer one comes
   */
  async get(url: string, config?: AxiosRequestConfig, id?: string) {
    if (!id) {
      return this.httpClient.get(url, config);
    }

    const activeCancelManager = this.activeCancelManagersById.get(id);
    if (activeCancelManager) {
      activeCancelManager.cancel();
    }

    const newCancelManager = axios.CancelToken.source();
    this.activeCancelManagersById.set(id, newCancelManager);
    try {
      const response = await this.httpClient.get(url, {
        ...config,
        cancelToken: newCancelManager.token,
      });

      this.activeCancelManagersById.delete(id);
      return response;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.activeCancelManagersById.delete(id);
      }
      throw error;
    }
  }

  post(...args: Parameters<typeof axios.post>) {
    return this.httpClient.post(...args);
  }

  put(...args: Parameters<typeof axios.put>) {
    return this.httpClient.put(...args);
  }
}

const http = new HttpClientWrapper({});

export function isCancelError(error: Error) {
  return axios.isCancel(error);
}

export const UI_SERIALIZER_REQUEST_OPTIONS = {
  headers: {
    Accept: 'application/vnd+inspire.record.ui+json',
  },
};

export default http;
